import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  CreditCard,
  Check,
  Loader2,
  AlertTriangle,
  ShieldCheck,
  CalendarClock,
  Users,
  Building2,
  Unlink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import {
  billingCheckout,
  billingPortal,
  billingSyncSession,
  creditsPricing,
  orgUpgradeToUnternehmer,
  orgDecouple,
} from "@/lib/api";
import WalletBilling from "@/components/WalletBilling";
import { toast } from "sonner";

const PLANS = [
  { id: "monthly", name: "Monatlich", price: "€ 2", period: "/ Monat", note: "Monatlich kündbar" },
  { id: "yearly", name: "Jährlich", price: "€ 20", period: "/ Jahr", note: "2 Monate geschenkt" },
];

const eur = (n) =>
  Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function Billing({ embedded = false }) {
  const { user, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState("");
  const [pricing, setPricing] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [confirmUpgrade, setConfirmUpgrade] = useState(false);
  const [decoupling, setDecoupling] = useState(false);

  const status = params.get("status");

  const accountType = user?.account_type || "einzelnutzer";
  const isEinzel = !user?.exempt && !user?.is_org_payer && accountType === "einzelnutzer";

  useEffect(() => {
    if (isEinzel) {
      creditsPricing()
        .then(setPricing)
        .catch(() => {});
    }
  }, [isEinzel]);

  const handleSuccess = useCallback(async () => {
    const sid = params.get("session_id");
    if (sid) {
      try {
        await billingSyncSession(sid);
      } catch {
        /* webhook will reconcile shortly */
      }
    }
    await refresh();
    toast.success("Vielen Dank! Ihr Abo ist aktiv.");
    setParams({}, { replace: true });
  }, [params, refresh, setParams]);

  useEffect(() => {
    if (status === "success") {
      handleSuccess();
    } else if (status === "cancel") {
      toast.info("Vorgang abgebrochen.");
      setParams({}, { replace: true });
    }
  }, [status, handleSuccess, setParams]);

  const subscribe = async (plan) => {
    setBusy(plan);
    try {
      const { url } = await billingCheckout(plan, window.location.origin);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout konnte nicht gestartet werden.");
      setBusy("");
    }
  };

  const doDecouple = async () => {
    setDecoupling(true);
    try {
      await orgDecouple();
      await refresh();
      toast.success(
        "Verbindung zur Kanzlei getrennt. Sie verwalten und bezahlen Ihr Konto ab sofort selbst."
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trennen fehlgeschlagen.");
    } finally {
      setDecoupling(false);
    }
  };

  const doUpgrade = async () => {
    setUpgrading(true);
    try {
      const res = await orgUpgradeToUnternehmer();
      await refresh();
      const voucher = Number(res?.voucher || 0);
      if (voucher > 0) {
        toast.success(
          `Upgrade erfolgreich! Ihnen wurden ${eur(voucher)} € Restguthaben aus Ihrem bisherigen Abo gutgeschrieben.`
        );
      } else {
        toast.success("Upgrade erfolgreich! Ihr Konto ist jetzt ein Unternehmer-Konto.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upgrade fehlgeschlagen.");
    } finally {
      setUpgrading(false);
      setConfirmUpgrade(false);
    }
  };

  const openPortal = async () => {    setBusy("portal");
    try {
      const { url } = await billingPortal(window.location.origin);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verwaltung konnte nicht geöffnet werden.");
      setBusy("");
    }
  };

  const exempt = user?.exempt;
  const subStatus = user?.subscription_status || "";
  const active = ["active", "trialing"].includes(subStatus);
  const pastDue = subStatus === "past_due";
  const hasSub = !!user?.has_subscription;
  const requires = !!user?.requires_subscription;
  const inTrial = subStatus === "trial" && !requires;
  const trialDays = user?.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / 86400000))
    : 0;

  const showManage = hasSub && (active || pastDue);
  const showPlans = isEinzel && !active && !pastDue;

  // Verwaltete Sub-Konten (Mandant unter Kanzlei, Mitarbeiter, Kanzlei-Mitarbeiter):
  // Abrechnung läuft über den Kontoinhaber/die Kanzlei → keine Kaufmöglichkeit, nur Hinweis.
  const isManaged = !exempt && !user?.is_org_payer && !!user?.parent_id;
  if (isManaged) {
    const at = user?.account_type;
    const hint =
      at === "unternehmer"
        ? "Ihr Konto ist mit einer Steuerberatungskanzlei verbunden. Die Nutzung wird über Ihre Kanzlei abgerechnet – für Sie fällt keine separate Zahlung an."
        : at === "kanzlei_mitarbeiter"
          ? "Ihr Konto gehört zu einer Steuerberatungskanzlei. Die Abrechnung übernimmt die Kanzlei – für Sie fällt keine Zahlung an."
          : "Ihr Konto wird von Ihrem Unternehmen verwaltet. Es ist keine eigene Zahlung erforderlich.";
    return (
      <div
        className={embedded ? "space-y-6" : "max-w-3xl mx-auto space-y-8"}
        data-testid="billing-page"
      >
        {!embedded && (
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <CreditCard className="h-7 w-7 text-primary" /> Abo &amp; Zahlung
            </h1>
            <p className="text-muted-foreground mt-2">Verwalten Sie Ihr BeSca-Abonnement.</p>
          </div>
        )}
        <div
          className="rounded-lg border border-border bg-white p-6 flex items-start gap-3"
          data-testid="billing-managed"
        >
          <ShieldCheck className="h-6 w-6 text-primary shrink-0" />
          <div>
            <p className="font-semibold">Konto aktiv</p>
            <p className="text-sm text-muted-foreground">{hint}</p>
          </div>
        </div>

        {at === "unternehmer" && (
          <div
            className="rounded-lg border border-destructive/30 bg-destructive/5 p-6"
            data-testid="decouple-card"
          >
            <div className="flex items-center gap-2 mb-2">
              <Unlink className="h-5 w-5 text-destructive shrink-0" />
              <p className="font-semibold">Verbindung zur Kanzlei</p>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Solange Ihr Konto mit der Steuerberatungskanzlei verbunden ist, übernimmt diese die
              Abrechnung. Wenn Sie sich entkoppeln, verliert die Kanzlei den Zugriff auf Ihr Konto
              und Sie verwalten und bezahlen Ihr Konto ab sofort eigenständig.
            </p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="secondary"
                  className="border border-destructive/40 text-destructive hover:bg-destructive hover:text-white gap-2"
                  data-testid="decouple-button"
                >
                  <Unlink className="h-4 w-4" /> Vom Steuerberater entkoppeln
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="decouple-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Verbindung wirklich trennen?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Die Kanzlei verliert sofort den vollständigen Zugriff auf Ihr Konto und wird per
                    E-Mail informiert. Ihre Mitarbeiter bleiben Ihnen zugeordnet. Diese Aktion kann
                    nur durch eine neue Einladung der Kanzlei rückgängig gemacht werden.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="decouple-cancel">Abbrechen</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={doDecouple}
                    disabled={decoupling}
                    className="bg-destructive hover:bg-destructive/90"
                    data-testid="decouple-confirm"
                  >
                    {decoupling ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ja, trennen"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>
    );
  }

  // Org-Zahler (Steuerberater / eigenständige Unternehmer): Credit-Wallet statt Abo.
  if (!exempt && user?.is_org_payer) {
    return (
      <div
        className={embedded ? "space-y-6" : "max-w-3xl mx-auto space-y-8"}
        data-testid="billing-page"
      >
        {!embedded && (
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <CreditCard className="h-7 w-7 text-primary" /> Abo
            </h1>
            <p className="text-muted-foreground mt-2">
              Verwalten Sie Ihr Guthaben, Ihre Aufladungen und die Kostenprognose.
            </p>
          </div>
        )}
        <WalletBilling />
      </div>
    );
  }

  return (
    <div
      className={embedded ? "space-y-6" : "max-w-3xl mx-auto space-y-8"}
      data-testid="billing-page"
    >
      {!embedded && (
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <CreditCard className="h-7 w-7 text-primary" /> Abo &amp; Zahlung
          </h1>
          <p className="text-muted-foreground mt-2">Verwalten Sie Ihr BeSca-Abonnement.</p>
        </div>
      )}

      {exempt && (
        <div
          className="rounded-lg border border-border bg-white p-6 flex items-start gap-3"
          data-testid="billing-status"
        >
          <ShieldCheck className="h-6 w-6 text-primary shrink-0" />
          <div>
            <p className="font-semibold">Kein Abo erforderlich</p>
            <p className="text-sm text-muted-foreground">
              Ihr Konto ist von der Abrechnung ausgenommen.
            </p>
          </div>
        </div>
      )}

      {!exempt && pastDue && (
        <div
          className="rounded-lg border border-destructive bg-destructive/10 p-4 flex items-start gap-3"
          data-testid="billing-pastdue"
        >
          <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
          <div className="text-sm text-destructive">
            <p className="font-semibold">Zahlung fehlgeschlagen</p>
            <p>
              Bitte aktualisieren Sie Ihre Zahlungsdaten, um die Deaktivierung Ihres Kontos zu
              verhindern.
            </p>
          </div>
        </div>
      )}

      {!exempt && inTrial && (
        <div
          className="rounded-lg border border-border bg-primary/5 p-4 flex items-start gap-3"
          data-testid="billing-status"
        >
          <CalendarClock className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold">Kostenlose Testphase</p>
            <p className="text-muted-foreground">
              Noch <strong>{trialDays}</strong> {trialDays === 1 ? "Tag" : "Tage"} gratis (bis{" "}
              {fmtDate(user.trial_ends_at)}). Schließen Sie ein Abo ab, damit Ihr Zugang ohne
              Unterbrechung weiterläuft.
            </p>
          </div>
        </div>
      )}

      {!exempt && requires && !pastDue && (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
          data-testid="billing-status"
        >
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold">Ihre Testphase ist abgelaufen</p>
            <p>Bitte wählen Sie ein Abo, um BeSca weiter zu nutzen.</p>
          </div>
        </div>
      )}

      {isEinzel && (
        <div
          className="rounded-lg border border-border bg-white p-6 space-y-4"
          data-testid="einzel-subscription-content"
        >
          <div className="flex items-center gap-3">
            <Users className="h-6 w-6 text-primary" />
            <div>
              <p className="font-semibold">Ihr Abo: Einzelnutzer</p>
              <p className="text-sm text-muted-foreground" data-testid="einzel-sub-status">
                {active
                  ? "Status: aktiv"
                  : inTrial
                    ? `Status: Testphase – noch ${trialDays} ${trialDays === 1 ? "Tag" : "Tage"}`
                    : requires
                      ? "Status: Testphase abgelaufen"
                      : "Status: Testphase"}
                {active && user?.current_period_end
                  ? ` · Nächste Verlängerung: ${fmtDate(user.current_period_end)}`
                  : ""}
              </p>
            </div>
          </div>
          <ul className="text-sm text-muted-foreground space-y-1 list-disc pl-5">
            <li>Unbegrenzt Belege erfassen &amp; automatisch per KI auslesen</li>
            <li>PDF- &amp; Buchhaltungs-Export (CSV)</li>
            <li>Eigene Stammdaten &amp; MwSt-Aufschlüsselung</li>
          </ul>
        </div>
      )}

      {showManage && (
        <div
          className="rounded-lg border border-border bg-white p-6 space-y-4"
          data-testid="billing-active"
        >
          <div className="flex items-center gap-3">
            <Check className="h-6 w-6 text-primary" />
            <div>
              <p className="font-semibold">
                {active ? "Abo aktiv" : "Abo (Zahlung ausstehend)"}
              </p>
              {user?.current_period_end && (
                <p className="text-sm text-muted-foreground">
                  {active ? "Nächste Verlängerung" : "Laufende Periode bis"}:{" "}
                  {fmtDate(user.current_period_end)}
                </p>
              )}
            </div>
          </div>
          <Button
            onClick={openPortal}
            disabled={busy === "portal"}
            data-testid="manage-subscription-button"
          >
            {busy === "portal" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Abo verwalten"}
          </Button>
        </div>
      )}

      {showPlans && (
        <div className="grid sm:grid-cols-2 gap-4">
          {PLANS.map((p) => (
            <div
              key={p.id}
              className="rounded-lg border border-border bg-white p-6 flex flex-col"
              data-testid={`plan-${p.id}`}
            >
              <p className="text-sm font-medium text-muted-foreground">{p.name}</p>
              <p className="mt-2 text-3xl font-bold">
                {p.price}
                <span className="text-base font-normal text-muted-foreground"> {p.period}</span>
              </p>
              <p className="mt-1 text-sm text-primary">{p.note}</p>
              <Button
                className="mt-6"
                onClick={() => subscribe(p.id)}
                disabled={!!busy}
                data-testid={`subscribe-${p.id}-button`}
              >
                {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Abonnieren"}
              </Button>
            </div>
          ))}
        </div>
      )}

      {isEinzel && (
        <div
          className="rounded-lg border-2 border-primary/40 bg-primary/5 p-6 space-y-4"
          data-testid="upgrade-unternehmer-box"
        >
          <div className="flex items-center gap-3">
            <Building2 className="h-6 w-6 text-primary" />
            <p className="font-semibold text-lg">Upgrade auf Unternehmer</p>
          </div>
          <p className="text-sm text-muted-foreground">
            Sie können Ihr Einzelnutzer-Konto auf ein Unternehmer-Konto erweitern und Mitarbeiter
            dazu einladen oder sich mit einem Steuerberater, der BeSca Partner ist verbinden.
          </p>
          {pricing && (
            <div
              className="rounded-md border border-border bg-white p-4 text-sm space-y-1"
              data-testid="upgrade-pricing"
            >
              <p className="font-medium">Aktuelle Preislogik (Unternehmer)</p>
              <p>
                <strong>{eur(pricing.per_employee)} €</strong> pro Mitarbeiter / Monat
              </p>
              <p>
                Bis zu {pricing.mitarbeiter_min} Mitarbeiter gilt der Mindestpreis von{" "}
                <strong>{eur(pricing.min_price)} € / Monat</strong>.
              </p>
              <p className="text-muted-foreground">
                Die ersten {pricing.free_mitarbeiter} Mitarbeiter sind in der{" "}
                {pricing.org_trial_days}-tägigen Testphase kostenlos. Größere Teams profitieren
                von Staffelpreisen.
              </p>
            </div>
          )}
          <Button
            onClick={() => setConfirmUpgrade(true)}
            disabled={upgrading}
            data-testid="upgrade-unternehmer-button"
          >
            {upgrading ? <Loader2 className="h-4 w-4 animate-spin" /> : "UPGRADE UNTERNEHMER"}
          </Button>
        </div>
      )}

      <AlertDialog open={confirmUpgrade} onOpenChange={setConfirmUpgrade}>
        <AlertDialogContent data-testid="upgrade-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Auf Unternehmer-Konto upgraden?</AlertDialogTitle>
            <AlertDialogDescription>
              Ihr Konto wird in ein Unternehmer-Konto mit eigenem Guthaben-Konto (Wallet) und einer
              neuen {pricing?.org_trial_days ?? 30}-tägigen Gratis-Testphase umgewandelt. Sie können
              anschließend Mitarbeiter einladen. Ein bestehendes, bezahltes Einzelnutzer-Abo wird
              beendet – das anteilige Restguthaben wird Ihrem Guthaben-Konto gutgeschrieben.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="upgrade-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={doUpgrade} data-testid="upgrade-confirm">
              Jetzt upgraden
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <p className="text-xs text-muted-foreground">
        Die Zahlung erfolgt sicher über Stripe.{" "}
        {!embedded && (
          <Link to="/benutzer" className="underline">
            Zum Benutzerkonto
          </Link>
        )}
      </p>
    </div>
  );
}
